import React, { useState } from 'react';
// Added Bot icon here
import { Camera, Upload, CheckCircle, ArrowRight, Shield, Sparkles, Bot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import HeroSection from '../component/HeroSection';
import Footer from "../component/Footer";
import LegalModel from "../models/LegalModel";

const HomePage = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [expandedFaqIndex, setExpandedFaqIndex] = useState(null);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [currentLegalSection, setCurrentLegalSection] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const services = [
    { id: 'welfare', title: 'Social Welfare', price: 'FREE', description: 'Old age pension, disability and widow support', icon: '👵🏽', color: '#4CAF50' },
    { id: 'certificates', title: 'Certificates', price: '₹30', description: 'Tribe, income, domicile certificates', icon: '📜', color: '#FF9800' },
    { id: 'scholarships', title: 'Scholarships', price: '₹100', description: 'Buddy4Study and Scholarships.gov.in', icon: '🎓', color: '#2196F3' },
    { id: 'aappar', title: 'AAPPAR Registration', price: '₹50', description: 'Student registration assistance', icon: '📝', color: '#9C27B0' },
    { id: 'pancard', title: 'PAN Card', price: '₹100', description: 'Application and corrections', icon: '💳', color: '#F44336' }
  ];

  const faqs = [
    {
      question: "What is Naoko?",
      answer: "Naoko is a personal AI assistant that helps users apply for government documents and social welfare services across India through a simple chat interface."
    },
    {
      question: "How does Naoko work?",
      // Updated this FAQ answer slightly to reflect the new flow
      answer: "Start by chatting with Naoko to ask questions and check eligibility. Naoko will guide you on document uploads, use AI to fill forms, let you review, and then submit the application, keeping you updated."
    },
    {
      question: "Is Naoko free to use?",
      answer: "Many social welfare services are completely free. Other services have minimal processing fees (₹30-100) which are clearly displayed before confirmation."
    },
    {
      question: "Are my documents and payments secure?",
      answer: "Yes! All documents are encrypted and used only for your applications. Payments are processed through PCI-compliant gateways with bank-level security."
    }
  ];

  const handleCheckEligibility = (service) => {
    if (!user) {
      alert('Please log in or sign up to check eligibility.');
      // Consider navigating to login/signup page instead of alert
      // navigate('/login');
      return;
    }
    setSelectedService(service);
    // In a real app, you might navigate to a chat interface here instead of the upload modal directly
    // navigate(`/chat?service=${service?.id || 'general'}`);
    // For now, we keep the modal logic as it was, assuming chat happens elsewhere or this modal is part of the flow *after* initial chat
    setShowUploadModal(true);
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedService(null);
  };

  const openLegalModal = (section) => {
    setCurrentLegalSection(section);
    setLegalModalOpen(true);
  };

  const closeLegalModal = () => {
    setLegalModalOpen(false);
    setCurrentLegalSection(null);
  };

  const toggleFaq = (index) => {
    setExpandedFaqIndex(expandedFaqIndex === index ? null : index);
  };

  return (
    <div className="home-page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div style={{ flex: '1 0 auto' }}>
        <HeroSection onCheckEligibility={() => handleCheckEligibility(null)} />

        <section className="services-section">
          <div className="section-container">
            <div className="section-header">
              <h2>Our Services</h2>
              <p className="section-subtitle">AI-powered assistance for all your government service needs</p>
            </div>
            <div className="services-grid">
              {services.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onClick={() => handleCheckEligibility(service)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* === HOW IT WORKS SECTION === */}
        <section className="how-it-works-section">
          <div className="section-container">
            <div className="section-header">
              <h2>How Naoko Works</h2>
              <p className="section-subtitle">Your simple, conversational path to government services</p>
            </div>
            {/* steps-container uses auto-fit grid layout like trust-points */}
            <div className="steps-container">
              {/* Step Components */}
              <Step
                number={1}
                icon={<Bot size={24} />}
                title="Chat with Naoko"
                description="Start by talking to Naoko. Ask about services, check eligibility, learn document needs, and begin your application—all via chat."
              />
              <Step
                number={2}
                icon={<Upload size={24} />}
                title="Upload Your Documents"
                description="Naoko will tell you exactly which documents are needed. Simply take a photo or upload the files."
              />
              <Step
                number={3}
                icon={<Sparkles size={24} />}
                title="AI Magic: Form Filling"
                description="Our smart AI reads your documents and automatically fills out the application forms accurately."
              />
              <Step
                number={4}
                icon={<CheckCircle size={24} />}
                title="Review & Confirm"
                description="Quickly check the details Naoko filled in to ensure everything is correct before submission."
              />
              <Step
                number={5}
                icon={<Shield size={24} />}
                title="Submit & Track Progress"
                description="Confirm to submit your application. Naoko handles the rest and keeps you updated on the status."
              />
            </div>
          </div>
        </section>
        {/* === END OF HOW IT WORKS SECTION === */}

        <section className="stats-section">
          <div className="section-container">
            <div className="stats-grid">
              <StatCard number="5000+" label="Applications Processed" />
              <StatCard number="98%" label="Success Rate" />
              <StatCard number="24/7" label="Support Available" />
            </div>
          </div>
        </section>

        <section className="trust-section">
          <div className="section-container">
            <div className="section-header">
              <h2>Why Choose Naoko</h2>
              <p className="section-subtitle">The trusted platform for government service assistance</p>
            </div>
            {/* trust-points is the reference layout */}
            <div className="trust-points">
              <TrustPoint
                icon={<CheckCircle size={32} />}
                title="Accurate & Fast"
                description="AI-powered extraction via Naoko RAG"
              />
              <TrustPoint
                icon={<Shield size={32} />}
                title="Secure & Private"
                description="Data protected with Naoko Backend"
              />
              <TrustPoint
                icon={<CheckCircle size={32} />}
                title="Affordable"
                description="Low-cost services, free welfare options"
              />
            </div>
          </div>
        </section>

        <section className="faq-section">
          <div className="section-container">
            <div className="section-header">
              <h2>Frequently Asked Questions</h2>
              <p className="section-subtitle">Quick answers to common questions</p>
            </div>
            <div className="faq-container">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${expandedFaqIndex === index ? 'expanded' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <div className="faq-question">
                    <h3>{faq.question}</h3>
                    <span className="faq-toggle">
                      {expandedFaqIndex === index ? '−' : '+'}
                    </span>
                  </div>
                  {expandedFaqIndex === index && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="faq-footer">
              <p>Still have questions? <button
                className="text-link"
                onClick={() => openLegalModal("faq")}
              >
                View full FAQ
              </button></p>
            </div>
          </div>
        </section>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
             <button className="modal-close" onClick={closeModal}>×</button>
              <h2>Upload Documents for {selectedService?.title || 'Service'}</h2>
              <p>Upload clear photos of your ID card (Aadhaar, Voter ID, etc.) as requested by Naoko.</p>
              <div className="upload-options">
                <button className="upload-option">
                  <Camera size={24} />
                  <span>Take Photo</span>
                </button>
                <button className="upload-option">
                  <Upload size={24} />
                  <span>Upload File</span>
                </button>
              </div>
              {selectedService?.id === 'scholarships' && (
                 <div className="additional-docs">
                   <p>For scholarships, Naoko may also ask for your Student ID & Marksheet.</p>
                 </div>
               )}
               {selectedService?.id === 'pancard' && (
                 <div className="additional-docs">
                   <p>For PAN Card, Aadhaar Card is usually required.</p>
                 </div>
               )}
              <div className="modal-actions">
                <button className="secondary-button" onClick={closeModal}>
                  Cancel
                </button>
                <button className="primary-button" disabled>
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {legalModalOpen && (
          <div className="modal-overlay" onClick={closeLegalModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeLegalModal}>×</button>
              <LegalModel section={currentLegalSection} />
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* --- Styles updated for How It Works section --- */}
      <style jsx>{`
        /* Global Page Styles */
        .home-page {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          color: #1a1a1a;
        }

        /* Section Container */
        .section-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 48px;
        }
        .section-header h2 {
          font-size: 2.5rem; font-weight: 700; color: #1a237e; margin-bottom: 12px;
          background: linear-gradient(90deg, #1a237e, #3949ab);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .section-subtitle {
          font-size: 1.1rem; color: #57597a; max-width: 700px;
          margin: 0 auto; line-height: 1.6;
        }

        /* Services Section */
        .services-section { padding: 80px 0; background: #f8fafd; }
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
        }

        /* How It Works Section */
        .how-it-works-section {
          padding: 80px 0; /* Consistent section padding */
          background: white;
        }

        /* === UPDATED steps-container STYLES (to match trust-points) === */
        .steps-container {
          display: grid;
          /* Use auto-fit like trust-points, adjust minmax */
          /* Using slightly smaller min width due to having 5 items */
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          /* Match gap with trust-points */
          gap: 32px;
          /* Match alignment */
          align-items: stretch;
        }
        /* Remove pseudo-element line */
        .steps-container::before {
            display: none;
        }
        /* === END UPDATED steps-container STYLES === */

        /* Statistics Section */
        .stats-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
          color: white;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 40px;
        }

        /* Trust Section */
        .trust-section {
          padding: 80px 0; /* Consistent section padding */
          background: #f8fafd;
        }
        .trust-points {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px; /* Reference gap */
          align-items: stretch; /* Reference alignment */
        }

        /* FAQ Section */
        .faq-section { padding: 80px 0; background: white; border-top: 1px solid rgba(0,0,0,0.05); }
        .faq-container { max-width: 800px; margin: 0 auto; }
        .faq-item {
          border-radius: 12px; margin-bottom: 16px; overflow: hidden;
          transition: all 0.3s ease; border: 1px solid rgba(0,0,0,0.05); cursor: pointer;
        }
        .faq-item:hover { border-color: rgba(0,0,0,0.1); }
        .faq-item.expanded { background: #f8fafd; border-color: rgba(26, 35, 126, 0.2); }
        .faq-question { padding: 20px; display: flex; justify-content: space-between; align-items: center; }
        .faq-question h3 { font-size: 1.1rem; font-weight: 600; color: #1a237e; margin: 0; flex: 1; }
        .faq-toggle {
          width: 24px; height: 24px; border-radius: 50%; background: #1a237e; color: white;
          display: flex; align-items: center; justify-content: center; font-weight: bold;
          font-size: 1.1rem; margin-left: 15px;
        }
        .faq-answer { padding: 0 20px 20px; color: #57597a; line-height: 1.6; }
        .faq-answer p { margin: 0; }
        .faq-footer { text-align: center; margin-top: 40px; color: #57597a; }
        .text-link {
          background: none; border: none; color: #1a237e; font-weight: 500; text-decoration: none;
          cursor: pointer; padding: 0; font-size: inherit;
        }
        .text-link:hover { text-decoration: underline; }

        /* Modal Styles */
        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 20px; animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modal-content {
          background: white; border-radius: 16px; padding: 30px; width: 100%; max-width: 800px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2); animation: modalSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1);
          position: relative; max-height: 90vh; overflow-y: auto;
        }
        @keyframes modalSlideUp { from { transform: translateY(30px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
        .modal-close {
           position: absolute; top: 15px; right: 15px; background: none; border: none; font-size: 24px;
           color: #999; cursor: pointer; padding: 5px; line-height: 1; transition: color 0.2s ease;
         }
         .modal-close:hover { color: #333; }

        /* Upload Modal Specific Styles */
        .upload-options { display: flex; gap: 16px; margin: 24px 0; }
        .upload-option {
          flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 12px; padding: 24px; border: 2px dashed #b0bec5; border-radius: 12px; background: #f8fafd;
          cursor: pointer; transition: all 0.3s ease; color: #1a237e; text-align: center; min-height: 120px;
        }
        .upload-option:hover { background: #e8eaf6; border-color: #1a237e; border-style: solid; transform: translateY(-2px); }
        .upload-option span { font-weight: 500; font-size: 0.9rem; }
        .upload-option.small { padding: 16px; gap: 8px; min-height: 100px; }
        .upload-option.small span { font-size: 0.8rem; }
        .additional-docs { margin-top: 24px; padding-top: 16px; border-top: 1px solid #e0e4ec; font-size: 0.9rem; color: #57597a; }
        .additional-docs p { margin-bottom: 12px; text-align: left; font-weight: 500; }
        .modal-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 32px; }

        /* Buttons */
        .primary-button, .secondary-button {
          padding: 12px 24px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.3s ease;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-size: 1rem;
          border: 2px solid transparent; line-height: 1.2;
        }
        .primary-button { background: #1a237e; color: white; border-color: #1a237e; box-shadow: 0 4px 12px rgba(26, 35, 126, 0.25); }
        .primary-button:hover:not(:disabled) { background: #0d1757; border-color: #0d1757; transform: translateY(-2px); box-shadow: 0 6px 16px rgba(26, 35, 126, 0.3); }
        .primary-button:disabled { background-color: #a0a0a0; border-color: #a0a0a0; color: #e0e0e0; cursor: not-allowed; transform: none; box-shadow: none; }
        .secondary-button { background: transparent; color: #1a237e; border-color: #1a237e; }
        .secondary-button:hover:not(:disabled) { background: rgba(26, 35, 126, 0.05); border-color: #0d1757; color: #0d1757; transform: translateY(-2px); }

        /* --- Responsive Design --- */

        @media (max-width: 1024px) {
          .section-header h2 { font-size: 2.2rem; }
          /* No specific grid changes needed for steps/trust points, auto-fit handles it */
        }

        @media (max-width: 768px) {
          .services-section, .how-it-works-section, .trust-section, .faq-section { padding: 60px 0; }
          .stats-section { padding: 80px 0; }
          .section-header { margin-bottom: 40px; }
          .section-header h2 { font-size: 2rem; }
          .section-subtitle { font-size: 1rem; }
          /* No specific grid changes needed for steps/trust points, auto-fit handles it */
          .stat-number { font-size: 2.8rem; }
          .stat-label { font-size: 1rem; }
        }

        @media (max-width: 480px) {
          .services-section, .how-it-works-section, .trust-section, .faq-section { padding: 48px 0; }
          .section-container { padding: 0 16px; }
          .section-header h2 { font-size: 1.8rem; }

          .services-grid,
          /* Apply consistent mobile layout to steps, trust points, stats */
          .steps-container,
          .trust-points,
          .stats-grid {
            grid-template-columns: 1fr; /* Single column */
            gap: 24px; /* Consistent mobile gap */
          }

          .service-card, .step, .trust-point {
             /* Consistent mobile padding */
            padding: 24px;
            text-align: center;
          }
          .step-icon { margin-left: auto; margin-right: auto; }

          .upload-options { flex-direction: column; }
          .modal-actions { flex-direction: column-reverse; gap: 10px; }
          .modal-actions .primary-button, .modal-actions .secondary-button { width: 100%; }
          .modal-content { padding: 20px; }
          .stat-card { margin-bottom: 20px; }
          .stat-card:last-child { margin-bottom: 0; }
        }
      `}</style>
    </div>
  );
};

// --- Sub-Components ---

const ServiceCard = ({ service, onClick }) => (
  <div
    className="service-card"
    onClick={onClick}
    style={{ '--service-color': service.color }}
    role="button"
    tabIndex={0}
    onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
  >
    <div className="service-icon">{service.icon}</div>
    <h3>{service.title}</h3>
    <div className={`service-price ${service.price === 'FREE' ? 'free' : ''}`}>
      {service.price}
    </div>
    <p>{service.description}</p>
    <div style={{ marginTop: 'auto', alignSelf: 'flex-start', color: '#1a237e', fontWeight: '600', fontSize: '0.9rem' }}>
      Check Eligibility <ArrowRight size={16} style={{ verticalAlign: 'middle', marginLeft: '4px' }} />
    </div>
    <style jsx>{`
      .service-card {
        background: white; border-radius: 16px; padding: 28px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05); transition: all 0.3s ease;
        display: flex; flex-direction: column; border-top: 4px solid var(--service-color, #1a237e);
        cursor: pointer; min-height: 220px;
      }
      .service-card:hover { transform: translateY(-5px); box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08); }
      .service-icon { font-size: 2.5rem; margin-bottom: 16px; line-height: 1; }
      .service-card h3 { font-size: 1.25rem; font-weight: 600; color: #1a237e; margin-bottom: 8px; }
      .service-price { font-size: 1rem; font-weight: 700; color: var(--service-color, #1a237e); margin-bottom: 12px; }
      .service-price.free { color: #4CAF50; }
      .service-card p { font-size: 0.9rem; color: #57597a; line-height: 1.6; margin-bottom: 16px; flex-grow: 1; }
       @media (max-width: 480px) { .service-card { padding: 24px; min-height: unset; } }
    `}</style>
  </div>
);

// Updated Step component styles to match TrustPoint padding and remove height
const Step = ({ number, icon, title, description }) => (
  <div className="step">
    <div className="step-number">{number}</div>
    <div className="step-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{description}</p>
    <style jsx>{`
      .step {
        background-color: #f8fafd; border-radius: 12px;
        /* Match TrustPoint padding */
        padding: 28px;
        text-align: center; position: relative; overflow: hidden;
        border: 1px solid rgba(26, 35, 126, 0.1);
        /* Removed height: 100% */
        display: flex; flex-direction: column;
      }
      .step-number {
        position: absolute; top: 12px; right: 12px; background: #1a237e; color: white;
        width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center;
        justify-content: center; font-weight: 600; font-size: 0.9rem;
      }
      .step-icon {
        margin-bottom: 16px; color: #1a237e; display: inline-block;
        background-color: rgba(26, 35, 126, 0.1); padding: 12px;
        border-radius: 50%; line-height: 1;
      }
      .step h3 {
        font-size: 1.1rem; font-weight: 600; color: #1a237e; margin-bottom: 8px; margin-top: 0;
      }
      .step p {
        font-size: 0.9rem; color: #57597a; line-height: 1.6; margin-bottom: 0; flex-grow: 1;
      }
      /* Mobile padding adjustment to match TrustPoint */
       @media (max-width: 480px) {
         .step {
           padding: 24px;
           text-align: center; /* Ensure center alignment persists */
         }
         .step-icon { margin-left: auto; margin-right: auto; }
       }
    `}</style>
  </div>
);

const StatCard = ({ number, label }) => (
  <div className="stat-card">
    <div className="stat-number">{number}</div>
    <div className="stat-label">{label}</div>
    <style jsx>{`
      .stat-card { text-align: center; }
      .stat-number {
        font-size: 3rem; font-weight: 700; line-height: 1.2; margin-bottom: 8px;
        background: linear-gradient(180deg, #ffffff 50%, #e1e6ff 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
      }
      .stat-label { font-size: 1.1rem; color: rgba(255, 255, 255, 0.85); }
       @media (max-width: 768px) { .stat-number { font-size: 2.8rem; } .stat-label { font-size: 1rem; } }
        @media (max-width: 480px) { .stat-number { font-size: 2.5rem; } }
    `}</style>
  </div>
);

// Updated TrustPoint styles for consistency (padding, align-items)
const TrustPoint = ({ icon, title, description }) => (
  <div className="trust-point">
    <div className="trust-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{description}</p>
    <style jsx>{`
      .trust-point {
        background: white; border-radius: 12px;
        /* Consistent padding */
        padding: 28px;
        text-align: center; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(0,0,0,0.05);
        display: flex; flex-direction: column; /* Ensure flex for alignment */
      }
      .trust-icon {
        margin-bottom: 16px; color: #1a237e; background-color: rgba(26, 35, 126, 0.1);
        padding: 14px; border-radius: 12px; display: inline-block; line-height: 1;
      }
       .trust-icon > svg { display: block; }
      .trust-point h3 {
        font-size: 1.15rem; font-weight: 600; color: #1a237e; margin-bottom: 8px; margin-top: 0;
      }
      .trust-point p {
        font-size: 0.9rem; color: #57597a; line-height: 1.6; margin-bottom: 0;
        flex-grow: 1; /* Allow description to grow */
      }
      /* Mobile padding adjustment */
       @media (max-width: 480px) {
         .trust-point {
           padding: 24px;
         }
       }
    `}</style>
  </div>
);

export default HomePage;